import clsx from 'clsx';
import { PauseCircle } from 'lucide-react';
import { Avatar } from '../Avatar';
import { ConfidenceBadge } from '../ConfidenceBadge';
import { EntityLink } from '../EntityLink';
import { useT } from '../../i18n';
import type { TranslationKey } from '../../i18n';
import { formatAge, formatDate, formatMoney } from '../../lib/format';
import { ageTone } from '../../lib/age';
import { stageShowsTimer, visibleInquiryAmount, type Inquiry, type InquirySource, type InquiryType } from '../../types/api';
import { nextStepBucket } from '../../lib/nextStep';
import { StageProgress } from './StageProgress';
import styles from './DealCard.module.css';

const SOURCE_CLASS: Record<InquirySource, string> = {
  end_customer: styles.srcEnd,
  integrator: styles.srcInt,
  partner_chkpz: styles.srcPart,
  email: styles.srcEmail,
};

const SOURCE_LABEL_KEY: Record<InquirySource, TranslationKey> = {
  end_customer: 'inquiries.sourceEndCustomer',
  integrator: 'inquiries.sourceIntegrator',
  partner_chkpz: 'inquiries.sourcePartnerChkpz',
  email: 'inquiries.sourceEmail',
};

export const TYPE_LABEL_KEY: Record<InquiryType, TranslationKey> = {
  QUESTION: 'inquiryType.question',
  REQUEST: 'inquiryType.request',
  INVITATION: 'inquiryType.invitation',
  PARTNERSHIP: 'inquiryType.partnership',
  SALE_OFFER: 'inquiryType.saleOffer',
  GISP: 'inquiryType.gisp',
  AUDIT_REQUEST: 'inquiryType.auditRequest',
  SOLUTION_HELP: 'inquiryType.solutionHelp',
};

export interface DealCardProps {
  inquiry: Inquiry;
  onOpen: (inquiry: Inquiry) => void;
  /** Muted style — used for paused deals inside the collapsed block. */
  dimmed?: boolean;
  /** Issue #67 — drained/greyscale style for the «Отбой» (lost) column: the
   *  card reads as inactive. Click/Enter still open it as usual. */
  muted?: boolean;
  /** HTML5 drag & drop (desktop enhancement; keyboard users keep the menu). */
  draggable?: boolean;
  dragging?: boolean;
  onDragStart?: (e: React.DragEvent<HTMLElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLElement>) => void;
  /** «Взять в работу» lives only in the detail drawer (#40) — these props
   *  are accepted for API compatibility but no longer rendered on the card. */
  canTake?: boolean;
  onTake?: (inquiry: Inquiry) => void;
}

/**
 * Board deal card: client, summary, amount (mono), source chip,
 * owner avatar, confidence badge, age. Whole card is one keyboard-focusable
 * action (opens the detail drawer). The client name is plain text — clicking
 * the card opens the deal, so a stray click can never navigate away to the
 * client card (issue #71). Integrator names in the footer stay drill-in links.
 */
export function DealCard({ inquiry, onOpen, dimmed, muted, draggable, dragging, onDragStart, onDragEnd }: DealCardProps) {
  const { t, locale } = useT();
  // Issue #50 — amount only from «ТКП» on, never a «0 ₽» placeholder.
  const amount = visibleInquiryAmount(inquiry);
  const managerName = inquiry.assignedManager?.name ?? null;
  const ownerName = managerName ?? inquiry.assignedIntegrator?.name ?? null;
  const isClosed = inquiry.outcome !== 'NONE';
  const onValidation = inquiry.stage === 'VALIDATION' && !isClosed;
  // Issue #63 — the card header: the deal's own title when set, otherwise the
  // client's company name (previous behaviour).
  const cardTitle = inquiry.cardTitle?.trim() || null;
  const integratorId = inquiry.assignedIntegrator?.id ?? inquiry.assignedIntegratorId;
  const tone = ageTone(inquiry.createdAt);
  // «Из заморозки» — latest event is a scheduler-driven RESUME (issue #25)
  const lastTransition = inquiry.transitions?.[0];
  const fromPause = !inquiry.paused && lastTransition?.type === 'RESUME' && lastTransition.fromPause === true;

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen(inquiry);
    }
  };

  return (
    <article
      className={clsx(styles.card, dimmed && styles.dimmed, muted && styles.muted, draggable && styles.draggable, dragging && styles.dragging)}
      role="button"
      tabIndex={0}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      aria-label={`${cardTitle ?? inquiry.client?.companyName ?? t('common.noClient')} — ${inquiry.summary}`}
      onClick={() => onOpen(inquiry)}
      onKeyDown={handleKey}
    >
      <div className={styles.top}>
        <div className={styles.client}>
          {cardTitle ? (
            <span className={styles.cardTitle}>{cardTitle}</span>
          ) : inquiry.client?.companyName ? (
            <span>{inquiry.client.companyName}</span>
          ) : (
            <span className={styles.unassigned}>{t('common.noClient')}</span>
          )}
        </div>
      </div>

      <p className={styles.summary}>{inquiry.summary}</p>

      {amount !== null && <div className={styles.sum}>{formatMoney(amount, locale)}</div>}

      <div className={styles.foot}>
        <span className={clsx(styles.src, SOURCE_CLASS[inquiry.source])}>
          {t(SOURCE_LABEL_KEY[inquiry.source] ?? 'inquiries.source')}
        </span>
        {inquiry.type && (
          <span className={clsx(styles.src, styles.typeChip)}>
            {t(TYPE_LABEL_KEY[inquiry.type] ?? 'inquiryType.question')}
          </span>
        )}
        {ownerName ? (
          <span className={styles.owner}>
            <Avatar name={ownerName} size="sm" />
            {!managerName && inquiry.assignedIntegrator ? (
              <EntityLink
                kind="integrator"
                id={integratorId}
                name={inquiry.assignedIntegrator.name}
                className={styles.ownerName}
              />
            ) : (
              <span className={styles.ownerName}>{ownerName}</span>
            )}
          </span>
        ) : (
          <span className={clsx(styles.owner, styles.unassigned)}>{t('common.notAssigned')}</span>
        )}
        {onValidation && (
          <span
            className={clsx(styles.takenChip, managerName ? styles.takenYes : styles.takenNo)}
            title={t('inquiries.fieldManager')}
          >
            {managerName ? t('inquiries.taken') : t('inquiries.notTaken')}
          </span>
        )}
        <span className={styles.metaRight}>
          {/* Issue #52 — next-step date chip; overdue/today are accented. */}
          {!isClosed && inquiry.nextStepAt && (
            <span
              className={clsx(
                styles.nextStep,
                nextStepBucket(inquiry.nextStepAt) === 'overdue' && styles.nextStepOverdue,
                nextStepBucket(inquiry.nextStepAt) === 'today' && styles.nextStepToday,
              )}
              title={t('inquiries.fieldNextStep')}
              aria-label={`${t('inquiries.fieldNextStep')}: ${formatDate(inquiry.nextStepAt, locale)}`}
            >
              {formatDate(inquiry.nextStepAt, locale)}
            </span>
          )}
          {fromPause && (
            <span className={styles.fromPauseBadge}>{t('inquiries.fromPauseBadge')}</span>
          )}
          {inquiry.paused && (
            <>
              {inquiry.pauseRemindAt && (
                <span className={styles.remindAt} title={t('inquiries.pauseRemindLabel')}>
                  {t('inquiries.remindUntil', { date: formatDate(inquiry.pauseRemindAt, locale) })}
                </span>
              )}
              <PauseCircle
                size={14}
                className={styles.pausedIcon}
                aria-label={t('stage.paused')}
              />
            </>
          )}
          <ConfidenceBadge confidence={inquiry.confidence} />
          {/* Issue #51 — day counter only from «ТКП»; early stages show quiet progress dots. */}
          {isClosed ? (
            <span className={styles.age}>{t('inquiries.closedLabel')}</span>
          ) : stageShowsTimer(inquiry.stage) ? (
            <span className={clsx(styles.age, styles[`age_${tone}`])}>
              {formatAge(inquiry.createdAt, locale)}
            </span>
          ) : (
            <StageProgress stage={inquiry.stage} />
          )}
        </span>
      </div>
    </article>
  );
}
